/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の動く図の線。札の出入り口、線の引き方（まっすぐ・逆向き・札の外を回す）、回す線の名前の置き場所、React Flow の既定色を配色トークンに差し替える class を持つ
 * @graph-connects none
 */
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
} from '@xyflow/react';
import {
  bdLoopLabelPlacement,
  bdLoopOffset,
  type BdEdgeRoute,
  type BdLoopLabelPlacement,
  type BdLoopSide,
} from './bd-graph-layout.js';

/**
 * 札の線の出入り口。`rev*` は後ろの列から前の列へ引く線、`around*` は札の下（縦の図では右）を、
 * `side*` は列の脇（横の図では左、縦の図では上）を回す線に使う（引き方は `BdEdgeRoute`）。
 * @graph-connects none
 */
export const HANDLE = {
  in: 'in',
  out: 'out',
  revIn: 'rev-in',
  revOut: 'rev-out',
  aroundIn: 'around-in',
  aroundOut: 'around-out',
  sideIn: 'side-in',
  sideOut: 'side-out',
} as const;

/** 回す線に渡すもの。 @graph-connects none */
interface BdLoopEdgeData extends Record<string, unknown> {
  side: BdLoopSide;
  lane: number;
}

/** 回す線。 @graph-connects none */
type BdLoopFlowEdge = Edge<BdLoopEdgeData, 'bdLoop'>;

/**
 * 引き方ごとの出入り口と線の種類。
 * @graph-connects none
 */
export function edgeRouting(
  route: BdEdgeRoute,
  lane: number,
  lr: boolean,
): Partial<Pick<Edge, 'type' | 'sourceHandle' | 'targetHandle' | 'data'>> {
  switch (route) {
    case 'straight':
      return { sourceHandle: HANDLE.out, targetHandle: HANDLE.in };
    case 'reverse':
      return { sourceHandle: HANDLE.revOut, targetHandle: HANDLE.revIn };
    case 'around':
      return {
        type: 'bdLoop',
        sourceHandle: HANDLE.aroundOut,
        targetHandle: HANDLE.aroundIn,
        data: { side: lr ? 'bottom' : 'right', lane } satisfies BdLoopEdgeData,
      };
    case 'side':
      return {
        type: 'bdLoop',
        sourceHandle: HANDLE.sideOut,
        targetHandle: HANDLE.sideIn,
        data: { side: lr ? 'left' : 'top', lane } satisfies BdLoopEdgeData,
      };
    default:
      return {};
  }
}

/** 名前の札を置き場所のどちらへ寄せるか。 @graph-connects none */
const LOOP_LABEL_SHIFT: Readonly<Record<BdLoopLabelPlacement['align'], string>> = {
  center: 'translate(-50%, -50%)',
  start: 'translate(4px, -50%)',
  end: 'translate(calc(-100% - 4px), -50%)',
};

/**
 * 札の外を回す線。名前は線が外を通る区間の上に置く。長い名前は 1 行で切り、全文は title で読む。
 * @graph-connects bdLoopOffset [calls] 札から離れる距離
 * @graph-connects bdLoopLabelPlacement [calls] 名前の置き場所
 */
function BdLoopEdgeView({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  data,
  style,
}: EdgeProps<BdLoopFlowEdge>) {
  const side = data?.side ?? 'bottom';
  const lane = data?.lane ?? 0;
  const [path] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    offset: bdLoopOffset(lane),
    borderRadius: 8,
  });
  const at = bdLoopLabelPlacement({ side, lane, sourceX, sourceY, targetX, targetY });
  const text = typeof label === 'string' ? label : '';
  return (
    <>
      <BaseEdge id={id} path={path} style={style} />
      {text === '' ? null : (
        <EdgeLabelRenderer>
          <div
            title={text}
            className="nodrag nopan absolute truncate rounded bg-[color-mix(in_srgb,var(--color-bg-elevated)_80%,transparent)] px-1.5 py-0.5 text-[10px] leading-3 text-[var(--color-text-secondary)]"
            style={{
              maxWidth: at.maxWidth,
              transform: `translate(${String(at.x)}px, ${String(at.y)}px) ${LOOP_LABEL_SHIFT[at.align]}`,
            }}
          >
            {text}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

/** @graph-connects none */
export const BD_GRAPH_EDGE_TYPES: EdgeTypes = { bdLoop: BdLoopEdgeView };

/**
 * React Flow の既定色（白地）を PI Lab の配色トークンに差し替える。線の名前の地は少し透かし、
 * 下を通る線や点の格子が見えるようにする。
 * @graph-connects none
 */
export const BD_FLOW_THEME_CLASS = [
  '[--xy-edge-label-background-color:color-mix(in_srgb,var(--color-bg-elevated)_80%,transparent)]',
  '[--xy-edge-label-color:var(--color-text-secondary)]',
  '[--xy-edge-stroke:var(--color-border-hover)]',
  '[--xy-minimap-background-color:var(--color-bg-surface)]',
  '[--xy-minimap-mask-background-color:color-mix(in_srgb,var(--color-bg-base)_60%,transparent)]',
  '[--xy-minimap-mask-stroke-color:var(--color-border)]',
  '[--xy-controls-button-background-color:var(--color-bg-elevated)]',
  '[--xy-controls-button-background-color-hover:var(--color-bg-hover)]',
  '[--xy-controls-button-color:var(--color-text-secondary)]',
  '[--xy-controls-button-color-hover:var(--color-text-primary)]',
  '[--xy-controls-button-border-color:var(--color-border)]',
  '[--xy-background-pattern-color:var(--color-border)]',
  '[--xy-attribution-background-color:transparent]',
].join(' ');
